import { enumMembers, type ApiSchema } from "../apiSchema";
import { jsonBody, requestJson } from "../http/httpClient";
import {
  readArray,
  readBoolean,
  readInteger,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../http/jsonDecoding";
import { readStringList } from "./contentDecoders";

/**
 * Curation of a change set (`docs/components/curation-ui.md` CU-2, CU-5, CU-6): possible
 * duplicates of the terms it adds and their resolution, the review units the server
 * computes, and the per-unit marks. Every write names the head the curator saw.
 */

export const RESOLUTION_KINDS = enumMembers<ResolutionKind>({ keep_new: true, merge_into: true, drop_new: true });
export type ResolutionKind = ApiSchema<"ResolutionKind">;
export const FIELD_ACTIONS = enumMembers<FieldAction>({ add: true, replace: true, skip: true });
export type FieldAction = ApiSchema<"FieldAction">;
const CANDIDATE_STATES = enumMembers<CandidateState>({ open: true, resolved: true, superseded: true, dismissed: true });
export type CandidateState = ApiSchema<"CandidateState">;
const SOURCE_KINDS = enumMembers<CandidateSourceKind>({ builtin_lexical: true, tool_run: true, agent_turn: true, person: true });
export type CandidateSourceKind = ApiSchema<"CandidateSourceKind">;
const EVIDENCE_KINDS = enumMembers<EvidenceKind>({ lexical: true, semantic: true, structural: true, usage: true, rationale: true });
export type EvidenceKind = ApiSchema<"EvidenceKind">;
export const UNIT_KINDS = enumMembers<UnitKind>({ conversation: true, term: true, item: true, affected: true, check: true });
export type UnitKind = ApiSchema<"UnitKind">;
const UNIT_CHANGES = enumMembers<UnitChange>({ added: true, changed: true, removed: true });
export type BulkRefusal = ApiSchema<"BulkRefusal">;
const BULK_REFUSALS = enumMembers<BulkRefusal>({ not_a_change: true, finding: true, open_candidate: true, failing_check: true });
export type UnitChange = ApiSchema<"UnitChange">;
const MARK_VERDICTS = enumMembers<MarkVerdict>({ ok: true, needs_change: true });
export type MarkVerdict = ApiSchema<"MarkVerdict">;

export interface CandidateScores {
  readonly total: number;
  readonly lexical: number | null;
  readonly semantic: number | null;
  readonly structural: number | null;
}

export interface FieldChoice {
  readonly predicate: string;
  readonly action: FieldAction;
}

export interface CandidateResolution {
  readonly kind: ResolutionKind;
  readonly narrower: boolean;
  readonly module: string | null;
  readonly reason: string;
  readonly target: string;
  readonly resultHead: string | null;
  readonly items: readonly {
    readonly itemId: string;
    readonly kind: string;
    readonly name: string;
    readonly rewrittenUses: number;
    readonly removedTriples: number;
    readonly addedTriples: number;
  }[];
  readonly droppedTriples: readonly string[];
  readonly droppedTripleCount: number;
  readonly fieldOutcomes: readonly FieldChoice[];
  readonly actor: string;
  readonly at: string;
}

export interface TermCandidate {
  readonly candidateId: string;
  readonly term: string;
  readonly target: string;
  readonly scores: CandidateScores;
  readonly evidence: readonly { readonly kind: EvidenceKind; readonly text: string }[];
  readonly sources: readonly { readonly kind: CandidateSourceKind; readonly threadEntry: string | null; readonly person: string | null }[];
  readonly state: CandidateState;
  readonly resolution: CandidateResolution | null;
  readonly history: readonly { readonly state: CandidateState; readonly actor: string | null; readonly at: string; readonly reason: string }[];
}

export interface TermSummary {
  readonly iri: string;
  readonly kind: string;
  readonly label: string | null;
  readonly module: string;
  readonly moduleName: string;
}

export interface FieldValue {
  readonly text: string;
  readonly language: string | null;
  readonly isIri: boolean;
}

export interface FieldComparison {
  readonly predicate: string;
  readonly termValues: readonly FieldValue[];
  readonly targetValues: readonly FieldValue[];
  readonly differs: boolean;
  readonly defaultAction: FieldAction;
}

export interface CandidateView {
  readonly candidate: TermCandidate;
  readonly termSummary: TermSummary | null;
  readonly targetSummary: TermSummary | null;
  readonly fields: readonly FieldComparison[];
  readonly needsResolution: boolean;
  readonly suggestsMerge: boolean;
}

export interface CandidateListing {
  readonly head: string;
  readonly candidates: readonly CandidateView[];
  readonly modules: readonly { readonly itemId: string; readonly name: string }[];
  /** Open proposals scored below it may be kept in bulk (CU-9). */
  readonly bulkKeepThreshold: number;
}

export interface ReviewUnit {
  readonly key: string;
  readonly kind: UnitKind;
  readonly digest: string;
  readonly items: readonly { readonly kind: string; readonly itemId: string }[];
  readonly change: UnitChange | null;
  readonly term: string | null;
  readonly addedTriples: readonly string[];
  readonly removedTriples: readonly string[];
  readonly addedCount: number;
  readonly removedCount: number;
  /** Why module B doesn't mark it OK in bulk at this head; `null`: it may (CU-9). */
  readonly bulkRefusal: BulkRefusal | null;
}

export interface ReviewMark {
  readonly reviewer: string;
  readonly unitKey: string;
  readonly verdict: MarkVerdict;
  readonly commentId: string | null;
  readonly head: string;
  /** It counts at the shown head: made there, or the unit did not change since. */
  readonly current: boolean;
  readonly at: string;
}

export interface UnitsAndMarks {
  readonly head: string;
  readonly units: readonly ReviewUnit[];
  readonly marks: readonly ReviewMark[];
}

export interface ResolutionRequest {
  readonly expectedHead: string;
  readonly kind: ResolutionKind;
  readonly fieldChoices: readonly FieldChoice[];
  readonly narrower: boolean;
  readonly module: string | null;
  readonly reason: string;
}

function readNumber(object: JsonObject, member: string): number {
  const value = object[member];
  if (typeof value !== "number") {
    throw new Error(`Expected "${member}" to be a number.`);
  }
  return value;
}

function readNullableNumber(object: JsonObject, member: string): number | null {
  return object[member] === null || object[member] === undefined ? null : readNumber(object, member);
}

function readFieldChoice(value: unknown): FieldChoice {
  const choice = readObject(value, "field choice");
  return { predicate: readString(choice, "predicate"), action: readOneOf(choice, "action", FIELD_ACTIONS) };
}

function readResolution(value: unknown): CandidateResolution {
  const body = readObject(value, "resolution");
  return {
    kind: readOneOf(body, "kind", RESOLUTION_KINDS),
    narrower: readBoolean(body, "narrower"),
    module: readNullableString(body, "module"),
    reason: readString(body, "reason"),
    target: readString(body, "target"),
    resultHead: readNullableString(body, "result_head"),
    items: readArray(body["items"], "rewritten items", (item) => {
      const entry = readObject(item, "rewritten item");
      return {
        itemId: readString(entry, "item_id"),
        kind: readString(entry, "kind"),
        name: readString(entry, "name"),
        rewrittenUses: readInteger(entry, "rewritten_uses"),
        removedTriples: readInteger(entry, "removed_triples"),
        addedTriples: readInteger(entry, "added_triples"),
      };
    }),
    droppedTriples: readStringList(body, "dropped_triples"),
    droppedTripleCount: readInteger(body, "dropped_triple_count"),
    fieldOutcomes: readArray(body["field_outcomes"], "field outcomes", readFieldChoice),
    actor: readString(body, "actor"),
    at: readString(body, "at"),
  };
}

export function readTermCandidate(value: unknown): TermCandidate {
  const body = readObject(value, "term candidate");
  const scores = readObject(body["scores"], "scores");
  return {
    candidateId: readString(body, "candidate_id"),
    term: readString(body, "term"),
    target: readString(body, "target"),
    scores: {
      total: readNumber(scores, "total"),
      lexical: readNullableNumber(scores, "lexical"),
      semantic: readNullableNumber(scores, "semantic"),
      structural: readNullableNumber(scores, "structural"),
    },
    evidence: readArray(body["evidence"], "evidence", (item) => {
      const evidence = readObject(item, "evidence");
      return { kind: readOneOf(evidence, "kind", EVIDENCE_KINDS), text: readString(evidence, "text") };
    }),
    sources: readArray(body["sources"], "sources", (item) => {
      const source = readObject(item, "source");
      return {
        kind: readOneOf(source, "kind", SOURCE_KINDS),
        threadEntry: readNullableString(source, "thread_entry"),
        person: readNullableString(source, "person"),
      };
    }),
    state: readOneOf(body, "state", CANDIDATE_STATES),
    resolution: body["resolution"] === null ? null : readResolution(body["resolution"]),
    history: readArray(body["history"], "history", (item) => {
      const change = readObject(item, "state change");
      return {
        state: readOneOf(change, "state", CANDIDATE_STATES),
        actor: readNullableString(change, "actor"),
        at: readString(change, "at"),
        reason: readString(change, "reason"),
      };
    }),
  };
}

function readSummary(value: unknown): TermSummary | null {
  if (value === null) {
    return null;
  }
  const body = readObject(value, "term summary");
  return {
    iri: readString(body, "iri"),
    kind: readString(body, "kind"),
    label: readNullableString(body, "label"),
    module: readString(body, "module"),
    moduleName: readString(body, "module_name"),
  };
}

function readValues(value: unknown): readonly FieldValue[] {
  return readArray(value, "field values", (item) => {
    const entry = readObject(item, "field value");
    return { text: readString(entry, "text"), language: readNullableString(entry, "language"), isIri: readBoolean(entry, "is_iri") };
  });
}

export function readCandidateListing(value: unknown): CandidateListing {
  const body = readObject(value, "candidate listing");
  return {
    head: readString(body, "head"),
    candidates: readArray(body["candidates"], "candidates", (item) => {
      const view = readObject(item, "candidate view");
      return {
        candidate: readTermCandidate(view["candidate"]),
        termSummary: readSummary(view["term_summary"]),
        targetSummary: readSummary(view["target_summary"]),
        fields: readArray(view["fields"], "fields", (field) => {
          const comparison = readObject(field, "field comparison");
          return {
            predicate: readString(comparison, "predicate"),
            termValues: readValues(comparison["term_values"]),
            targetValues: readValues(comparison["target_values"]),
            differs: readBoolean(comparison, "differs"),
            defaultAction: readOneOf(comparison, "default_action", FIELD_ACTIONS),
          };
        }),
        needsResolution: readBoolean(view, "needs_resolution"),
        suggestsMerge: readBoolean(view, "suggests_merge"),
      };
    }),
    modules: readArray(body["modules"], "modules", (item) => {
      const module = readObject(item, "module");
      return { itemId: readString(module, "item_id"), name: readString(module, "name") };
    }),
    bulkKeepThreshold: readNumber(body, "bulk_keep_threshold"),
  };
}

export function readUnitsAndMarks(value: unknown): UnitsAndMarks {
  const body = readObject(value, "units");
  return {
    head: readString(body, "head"),
    units: readArray(body["units"], "units", (item) => {
      const unit = readObject(item, "unit");
      return {
        key: readString(unit, "key"),
        kind: readOneOf(unit, "kind", UNIT_KINDS),
        digest: readString(unit, "digest"),
        items: readArray(unit["items"], "unit items", (entry) => {
          const location = readObject(entry, "unit item");
          return { kind: readString(location, "kind"), itemId: readString(location, "item_id") };
        }),
        change: unit["change"] === null ? null : readOneOf(unit, "change", UNIT_CHANGES),
        term: readNullableString(unit, "term"),
        addedTriples: readStringList(unit, "added_triples"),
        removedTriples: readStringList(unit, "removed_triples"),
        addedCount: readInteger(unit, "added_count"),
        removedCount: readInteger(unit, "removed_count"),
        bulkRefusal: unit["bulk_refusal"] === null ? null : readOneOf(unit, "bulk_refusal", BULK_REFUSALS),
      };
    }),
    marks: readArray(body["marks"], "marks", (item) => {
      const mark = readObject(item, "mark");
      return {
        reviewer: readString(mark, "reviewer"),
        unitKey: readString(mark, "unit_key"),
        verdict: readOneOf(mark, "verdict", MARK_VERDICTS),
        commentId: readNullableString(mark, "comment_id"),
        head: readString(mark, "head"),
        current: readBoolean(mark, "current"),
        at: readString(mark, "at"),
      };
    }),
  };
}

const changeSetPath = (changeSetId: string): `/${string}` => `/content/change-sets/${encodeURIComponent(changeSetId)}`;

export function getCandidates(changeSetId: string, signal?: AbortSignal): Promise<CandidateListing> {
  return requestJson({ method: "GET", path: `${changeSetPath(changeSetId)}/candidates`, signal, decodeResponse: readCandidateListing });
}

export function proposeCandidate(
  changeSetId: string,
  proposal: { readonly head: string; readonly term: string; readonly target: string; readonly rationale: string },
): Promise<TermCandidate> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/candidates`,
    body: jsonBody(proposal),
    decodeResponse: readTermCandidate,
  });
}

/** One commit on `expectedHead`; returns the new head (or the same, if nothing changed). */
export function resolveCandidate(changeSetId: string, candidateId: string, request: ResolutionRequest): Promise<{ readonly candidate: TermCandidate; readonly head: string }> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/candidates/${encodeURIComponent(candidateId)}/resolve`,
    body: jsonBody({
      expected_head: request.expectedHead,
      kind: request.kind,
      field_choices: request.fieldChoices.map((choice) => ({ predicate: choice.predicate, action: choice.action })),
      narrower: request.narrower,
      module: request.module,
      reason: request.reason,
    }),
    decodeResponse: (body) => {
      const result = readObject(body, "resolution result");
      return {
        candidate: readTermCandidate(result["candidate"]),
        head: readString(readObject(result["change_set"], "change set view"), "head"),
      };
    },
  });
}

export function dismissCandidate(changeSetId: string, candidateId: string, reason: string): Promise<TermCandidate> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/candidates/${encodeURIComponent(candidateId)}/dismiss`,
    body: jsonBody({ reason }),
    decodeResponse: readTermCandidate,
  });
}

export function renameTerm(changeSetId: string, rename: { readonly expectedHead: string; readonly term: string; readonly newIri: string }): Promise<void> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/term-renames`,
    body: jsonBody({ expected_head: rename.expectedHead, term: rename.term, new_iri: rename.newIri }),
    decodeResponse: () => undefined,
  });
}

export function getUnits(changeSetId: string, signal?: AbortSignal): Promise<UnitsAndMarks> {
  return requestJson({ method: "GET", path: `${changeSetPath(changeSetId)}/units`, signal, decodeResponse: readUnitsAndMarks });
}

export function markUnit(
  changeSetId: string,
  mark: { readonly head: string; readonly unitKey: string; readonly verdict: MarkVerdict | "cleared"; readonly note: string },
): Promise<UnitsAndMarks> {
  return requestJson({
    method: "PUT",
    path: `${changeSetPath(changeSetId)}/marks`,
    body: jsonBody({ head: mark.head, unit_key: mark.unitKey, verdict: mark.verdict, note: mark.note }),
    decodeResponse: readUnitsAndMarks,
  });
}
